import PurchaseIcon from "@/assets/icons/Buy purchase download.svg?react";
import FollowUserIcon from "@/assets/icons/Follow user.svg?react";
import HeartLikeIcon from "@/assets/icons/Heart like.svg?react";
import MusicIcon from "@/assets/icons/Library audiolab music filled.svg?react";
import PhotoIcon from "@/assets/icons/Photo image gallery.svg?react";
import VideoIcon from "@/assets/icons/Video library media tutorial.svg?react";
import {
  FOR_ARTISTS,
  FOR_FANS,
  FOR_LABELS,
} from "@/components/marketing/audiences";
import SidebarGetStartedTracker from "@/components/audiolab/GetStartedTracker/SidebarGetStartedTracker";
import SidebarUnlockTracker from "@/components/audiolab/UploadUnlockTracker/SidebarUnlockTracker";
import {
  useAudioPlayerQueue,
  useAudioPlayerState,
} from "@/components/providers/AudioPlayerProvider/useAudioPlayer";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { useAudiolab } from "@/components/providers/AudiolabProvider/hooks";
import { Flex, Separator, Text, Tooltip } from "@radix-ui/themes";
import { useGateValue } from "@statsig/react-bindings";
import { FaUsers } from "react-icons/fa";
import { MdAttachMoney } from "react-icons/md";
import {
  useLayoutEffect,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import { FiHelpCircle } from "react-icons/fi";
import { useUserback } from "@/hooks/useUserback";
import { saveRedirect } from "@/lib/storage-utils";
import CreateMenu from "./CreateMenu";
import NavButton from "./NavButton";

type LeftNavProp = {
  type?: "account" | null;
  children?: React.ReactNode;
};

export default function LeftNav({ type, children }: LeftNavProp) {
  const navigate = useNavigate();
  const { open: openSupport } = useUserback();
  const { data: user } = useLoggedInUser();
  const isAuthenticated = user != null;
  // Signed out, ask to sign in here and continue to the page afterwards.
  const openLibraryPage = (
    to:
      | "/account/purchases"
      | "/account/liked-songs"
      | "/account/followed-artists",
  ) => {
    if (isAuthenticated) return navigate({ to });
    saveRedirect(to);
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, auth: "login" as const }),
    });
  };
  const matchRoute = useMatchRoute();
  const { allCreators } = useAudiolab();
  const [scrollContainerHeight, setScrollContainerHeight] = useState<
    number | null
  >(null);
  const displayLikedTracksButton = useGateValue("display_liked_tracks_button");
  const { currentTrack } = useAudioPlayerState();
  const { queue, hydrated, restoredFromStorage } = useAudioPlayerQueue();
  // Mirrors __root's AudioPlayer visibility so the pinned unlock meter clears
  // the fixed player bar only when it is actually on screen.
  const isAudioPlayerVisible =
    currentTrack !== null ||
    (hydrated && queue.length === 0 && restoredFromStorage);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const scrollContainer = document.querySelector<HTMLElement>(
      ".main-scroll-container",
    );
    if (!scrollContainer) return;

    const updateHeight = () => {
      const nextHeight = Math.round(
        scrollContainer.getBoundingClientRect().height,
      );
      setScrollContainerHeight((prev: number | null) =>
        prev === nextHeight ? prev : nextHeight,
      );
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, []);

  const columnHeight = scrollContainerHeight
    ? `calc(${scrollContainerHeight}px - var(--leftnav-offset, 25px))`
    : "calc(100vh - var(--leftnav-offset, 25px))";

  return (
    <div
      className="relative hidden lg:block w-[214px] -ml-[10px] shrink-0 self-stretch mr-[40px]"
      style={{ minHeight: scrollContainerHeight ?? "100vh" }}
    >
      <div
        aria-hidden
        className="absolute -left-[100vw] right-0 -top-[100px] bottom-0 bg-white border-r border-[rgba(5,12,43,0.06)]"
      />
      <Flex
        direction="column"
        style={{
          top: "var(--leftnav-offset, 25px)",
          // A definite height so the help button sits at the very bottom.
          height: columnHeight,
        }}
        className={`w-[214px] px-[10px] sticky overflow-hidden ${
          isAudioPlayerVisible ? "pb-[72px]" : "pb-[16px]"
        }`}
      >
        <Flex
          direction="column"
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden no-scrollbar"
        >
          {children}
          {type === "account" && (
            // shrink-0 on the children: compressing them clipped the Get started
            // tracker (overflow-hidden) instead of scrolling this column.
            <Flex
              direction="column"
              className="flex-1 min-h-0 gap-[4px] [&>*]:shrink-0"
            >
              {/* data-tour anchor: the guided tour spotlights this on step 1. */}
              <div data-tour="create" className="mb-[8px]">
                <CreateMenu />
              </div>

              <NavButton
                title="Audio"
                icon={<MusicIcon width={22} height={22} />}
                data-testid="nav-audio"
                isActive={
                  !!matchRoute({ to: "/audiolab" }) ||
                  !!matchRoute({ to: "/audiolab/tracks/$TRACK_ID" }) ||
                  // Releases is a tab inside the library now, so the Audio entry
                  // has to stay lit there — otherwise nothing in the nav is
                  // selected.
                  !!matchRoute({ to: "/audiolab/releases", fuzzy: true })
                }
                onClick={() => {
                  navigate({ to: "/audiolab" });
                  // Already on /audiolab with the profile editor open, the
                  // navigation is a no-op — tell the editor to yield (LUT-944).
                  window.dispatchEvent(new Event("lutely:audiolab-home"));
                }}
              />

              {/* No Releases entry: it lives in the Tracks/Releases tab row
              inside the library, so a nav copy was just duplicating it. */}

              <Tooltip content="Coming soon">
                <div>
                  <NavButton
                    title="Images"
                    icon={<PhotoIcon width={22} height={22} />}
                    disabled
                  />
                </div>
              </Tooltip>

              <Tooltip content="Coming soon">
                <div>
                  <NavButton
                    title="Video"
                    icon={<VideoIcon width={22} height={22} />}
                    disabled
                  />
                </div>
              </Tooltip>

              <Separator className="w-full my-[12px] bg-background-quarternary" />

              {/* Label-level, so it sits with Sales rather than in the library's
              tab row: switching artist re-scopes those tabs, not this. */}
              {allCreators.length >= 2 && (
                <NavButton
                  title={`Roster (${allCreators.length})`}
                  icon={<FaUsers size={22} />}
                  data-testid="nav-roster"
                  isActive={
                    !!matchRoute({ to: "/audiolab/roster", fuzzy: true })
                  }
                  onClick={() => navigate({ to: "/audiolab/roster" })}
                />
              )}

              {/* Payout Account left the nav: connecting the account lives on
              the Sales & Earnings page now (its setup banner / header link). */}
              <NavButton
                title="Sales & Earnings"
                // Same dollar icon as the profile menu's Sales & Earnings entry.
                icon={<MdAttachMoney size={22} />}
                data-testid="nav-sales"
                isActive={!!matchRoute({ to: "/audiolab/sales" })}
                onClick={() => navigate({ to: "/audiolab/sales" })}
              />
              {/* Account Info, Plan Details and How It Works left the sidebar
              (8/14 notes): the first two live in the profile menu's Account
              Settings; help is the button at the bottom. */}

              <Separator className="w-full my-[12px] bg-background-quarternary" />

              <SidebarGetStartedTracker />
              <div className="mt-auto">
                <SidebarUnlockTracker />
              </div>
            </Flex>
          )}
          {type !== "account" && (
            <Flex direction="column" className="gap-[4px] pb-[16px]">
              <Text className="text-text-secondary text-[14px] mb-[5px]">
                Your Library
              </Text>
              <NavButton
                title="Purchases"
                icon={<PurchaseIcon width={22} height={22} />}
                isActive={!!matchRoute({ to: "/account/purchases" })}
                onClick={() => openLibraryPage("/account/purchases")}
              />
              {displayLikedTracksButton && (
                <NavButton
                  title="Liked Songs"
                  icon={
                    <HeartLikeIcon fill="currentColor" width={22} height={22} />
                  }
                  isActive={!!matchRoute({ to: "/account/liked-songs" })}
                  onClick={() => openLibraryPage("/account/liked-songs")}
                />
              )}
              <NavButton
                title="Followed Artists"
                icon={
                  <FollowUserIcon fill="currentColor" width={22} height={22} />
                }
                isActive={!!matchRoute({ to: "/account/followed-artists" })}
                onClick={() => openLibraryPage("/account/followed-artists")}
              />

              <Separator className="w-full my-[12px] bg-background-quarternary" />

              <Text className="text-text-secondary text-[14px] mb-[5px]">
                How It Works
              </Text>
              {[FOR_FANS, FOR_LABELS, FOR_ARTISTS].map(
                ({ label, to, Icon }) => (
                  <NavButton
                    key={to}
                    title={label}
                    icon={<Icon size={22} />}
                    isActive={!!matchRoute({ to })}
                    onClick={() => navigate({ to })}
                  />
                ),
              )}
            </Flex>
          )}

          {/* Terms/policies left the sidebar too — the site footer links them. */}
        </Flex>

        {/* Fixed under everything that scrolls, so it never covers the unlocks
          meter or the Get started checklist. */}
        <div className="shrink-0 border-t border-background-quarternary pt-[8px]">
          {/* Same look as the NavButtons above, so the icon lines up. */}
          <button
            type="button"
            onClick={openSupport}
            data-testid="nav-help"
            className="flex h-[35px] w-full cursor-pointer items-center gap-[8px] rounded-[5px] pl-[16px] text-[14px] font-semibold text-text-secondary transition-all hover:bg-background-quarternary hover:text-brand-primary"
          >
            <FiHelpCircle size={22} className="shrink-0" aria-hidden />
            Contact Support
          </button>
        </div>
      </Flex>
    </div>
  );
}
